
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { X, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Practitioner } from '@/types/practitioner';
import { getFullName } from '@/utils/name-helpers';
import { PractitionerSEO } from '@/components/seo/PractitionerSEO';
import { PractitionerShareButton } from '@/components/practitioner/PractitionerShareButton';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PractitionerHeader from './PractitionerHeader';
import PractitionerAbout from './PractitionerAbout';
import PractitionerEducation from './PractitionerEducation';

import PractitionerServices from './PractitionerServices';
import PractitionerReviews from './PractitionerReviews';
import PractitionerPhotoGallery from './PractitionerPhotoGallery';
import { normalizeSessionFormats } from '@/utils/session-format-helpers';

interface PractitionerDetailProps {
  practitioner?: Practitioner;
  onClose?: () => void;
  isFullPage?: boolean;
}

const PractitionerDetail: React.FC<PractitionerDetailProps> = ({ 
  practitioner: propPractitioner, 
  onClose,
  isFullPage = false 
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('practitioners');
  const [selectedService, setSelectedService] = useState<string | null>(null);

  // Scroll to top when component mounts (for full page)
  useEffect(() => {
    if (isFullPage) {
      window.scrollTo(0, 0);
    }
  }, [isFullPage]);

  const { data: practitioner, isLoading, error } = useQuery({
    queryKey: ['practitioner', id],
    queryFn: async () => {
      if (!id) throw new Error('No practitioner ID provided');
      
      // Try to fetch by slug first, then by UUID
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      const column = isUUID ? 'id' : 'slug';
      
      const { data, error } = await supabase
        .rpc('get_public_practitioners')
        .eq(column, id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Practitioner not found');
      
      // If accessed by UUID, redirect to slug URL
      if (isUUID && data.slug) {
        window.history.replaceState(null, '', `/book-an-appointment/${data.slug}`);
      }
      
      // Fetch services and timezone for this practitioner
      const [servicesData, timezoneData] = await Promise.all([
        supabase
          .from('services')
          .select('*')
          .eq('practitioner_id', data.id)
          .eq('is_active', true),
        supabase
          .from('practitioners')
          .select('timezone')
          .eq('id', data.id)
          .single()
      ]);

      if (servicesData.error) {
        console.error("Error fetching services:", servicesData.error);
      }
      
      if (timezoneData.error) {
        console.error("Error fetching timezone:", timezoneData.error);
      }

      const normalizedServices = (servicesData.data || []).map((s: any) => ({
        ...s,
        formats_available: normalizeSessionFormats(s.formats_available || [])
      }));
      const sessionFormatsUnion = Array.from(
        new Set(normalizedServices.flatMap((s: any) => s.formats_available || []))
      );

      // Fetch certifications and photos for this practitioner
      const [certificationsResult, photosResult] = await Promise.all([
        supabase
          .from('practitioner_certifications')
          .select('*')
          .eq('practitioner_id', data.id)
          .eq('status', 'approved')
          .order('created_at', { ascending: false }),
        supabase
          .from('practitioner_photos')
          .select('*')
          .eq('practitioner_id', data.id)
          .eq('is_active', true)
          .order('display_order', { ascending: true })
      ]);

      if (certificationsResult.error) {
        console.error("Error fetching certifications:", certificationsResult.error);
      }
      
      if (photosResult.error) {
        console.error("Error fetching photos:", photosResult.error);
      }

      return { 
        ...data, 
        // Fill required fields not exposed in public view with safe defaults
        in_person_address: (data as any).in_person_address ?? null,
        is_verified: true,
        profile_status: 'approved',
        timezone: timezoneData.data?.timezone || 'UTC',
        gender: (data as any).gender ?? null,
        services: normalizedServices,
        // Override practitioner-level formats with union of active service formats
        session_formats_offered: sessionFormatsUnion,
        certifications: certificationsResult.data || [],
        practitioner_photos: photosResult.data || []
      } as Practitioner;
    },
    enabled: !!id && !propPractitioner,
  });

  // Use provided practitioner or fetched one
  const currentPractitioner = propPractitioner || practitioner;

  const handleGoBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleBookService = (serviceId: string) => {
    if (!currentPractitioner) return;
    
    navigate('/payment', { 
      state: { 
        practitionerId: currentPractitioner.id,
        serviceId,
        practitionerName: getFullName(currentPractitioner),
        serviceName: currentPractitioner.services?.find(s => s.id === serviceId)?.service_name
      }
    });
  };

  // Show loading for full page view
  if (isFullPage && (isLoading || !currentPractitioner)) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-20">
          <div className="container mx-auto max-w-4xl">
            <div className="mb-6">
              <Skeleton className="h-10 w-32" />
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="space-y-6">
                <Skeleton className="h-24 w-24 rounded-full" />
                <Skeleton className="h-8 w-1/2" />
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show error for full page view
  if (isFullPage && (error || !currentPractitioner)) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-20">
          <div className="container mx-auto max-w-4xl">
            <Button 
              variant="ghost" 
              onClick={handleGoBack}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <h3 className="text-lg font-medium text-red-700 mb-1">Practitioner Not Found</h3>
              <p className="text-red-600">The practitioner you're looking for doesn't exist or has been removed.</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Return early if no practitioner for inline view
  if (!currentPractitioner) {
    return null;
  }

  const practitionerName = getFullName(currentPractitioner);

  // Render full page layout
  if (isFullPage) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        {currentPractitioner && <PractitionerSEO practitioner={currentPractitioner} />}
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-20">
          <div className="container mx-auto max-w-6xl px-2 sm:px-4">
            {/* Action Bar */}
            <div className="flex items-center justify-between mb-6">
              <Button 
                variant="ghost" 
                onClick={handleGoBack}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              {currentPractitioner && (
                <PractitionerShareButton practitioner={currentPractitioner} />
              )}
            </div>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6 space-y-6">
                <PractitionerHeader practitioner={currentPractitioner} />
                <Separator />
                <PractitionerAbout bio={currentPractitioner.bio} />
                <PractitionerEducation 
                  education={currentPractitioner.education}
                  certifications={currentPractitioner.certifications || []}
                />
                
                {/* Photo Gallery */}
                {(currentPractitioner as any).practitioner_photos && (currentPractitioner as any).practitioner_photos.length > 0 && (
                  <>
                    <PractitionerPhotoGallery 
                      photos={(currentPractitioner as any).practitioner_photos} 
                    />
                    <Separator />
                  </>
                )}
                
                <PractitionerServices
                  services={currentPractitioner.services || []}
                  selectedService={selectedService}
                  onServiceSelect={setSelectedService}
                  onBookService={handleBookService}
                  practitionerId={currentPractitioner.id}
                  practitionerName={practitionerName}
                  professionalTitle={currentPractitioner.professional_title}
                  avatarUrl={currentPractitioner.avatar_url}
                />
                <PractitionerReviews
                  practitionerId={currentPractitioner.id}
                  averageRating={currentPractitioner.average_rating}
                  totalReviews={currentPractitioner.total_reviews}
                />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Render inline layout
  return (
    <div className="relative max-w-full mx-auto px-2 md:px-4 md:max-w-4xl">
      {/* Close Button */}
      {onClose && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="absolute top-4 right-4 z-10"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      <div className="p-6 space-y-6">
        <PractitionerHeader practitioner={currentPractitioner} />
        <Separator />
        <PractitionerAbout bio={currentPractitioner.bio} />
        <PractitionerEducation 
          education={currentPractitioner.education}
          certifications={currentPractitioner.certifications || []}
        />
        
        {/* Photo Gallery */}
        {(currentPractitioner as any).practitioner_photos && (currentPractitioner as any).practitioner_photos.length > 0 && (
          <>
            <PractitionerPhotoGallery 
              photos={(currentPractitioner as any).practitioner_photos} 
            />
            <Separator />
          </>
        )}
        
        <PractitionerServices
          services={currentPractitioner.services || []}
          selectedService={selectedService}
          onServiceSelect={setSelectedService}
          onBookService={handleBookService}
          practitionerId={currentPractitioner.id}
          practitionerName={practitionerName}
          professionalTitle={currentPractitioner.professional_title}
          avatarUrl={currentPractitioner.avatar_url}
        />
      </div>
    </div>
  );
};

export default PractitionerDetail;
